import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Users, Bot, History, Minus, Zap, Hand } from "lucide-react";

export default function AdminGlobalPools() {
  const { data, loading, reload } = useFetch("/admin/global-pools");
  const { data: analytics } = useFetch("/admin/pool-analytics");
  const { data: payoutMode, reload: reloadPayoutMode } = useFetch("/admin/global-pool-withdrawal-mode");
  const [inputs, setInputs] = useState({});
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const { data: poolWdData, reload: reloadPoolWds } = useFetch("/admin/global-pool-withdrawals");
  const [manualTx, setManualTx] = useState({});
  const [payoutBusy, setPayoutBusy] = useState(null);
  const { data: audit } = useFetch(auditFor ? `/admin/global-pools/${auditFor}/audit` : null, [auditFor]);

  const pools = data?.pools || [];
  const pendingPoolWithdrawals = (poolWdData?.withdrawals || []).filter((w) => ["requested", "processing"].includes(w.status));

  const processPoolWithdrawal = async (wd, action) => {
    let tx_hash;
    if (action === "paid" && !payoutMode?.auto) {
      tx_hash = (manualTx[wd.pw_id] || "").trim();
      if (!/^0x[a-fA-F0-9]{64}$/.test(tx_hash)) { toast.error("Manual mode requires the real BEP20 transaction hash"); return; }
    }
    if (action === "paid" && payoutMode?.auto && !window.confirm("Send $" + wd.net + " USDT on BEP20 to " + wd.wallet_address + " now?")) return;
    setPayoutBusy(wd.pw_id);
    try {
      const r = await api.post("/admin/global-pool-withdrawals/" + wd.pw_id + "/action", { action, tx_hash });
      toast.success(r.data.message);
      setManualTx((s) => ({ ...s, [wd.pw_id]: "" }));
      reloadPoolWds();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Payout action failed");
      reloadPoolWds();
    } finally { setPayoutBusy(null); }
  };
  const togglePoolPayoutMode = async () => {
    const next = !payoutMode?.auto;
    try {
      await api.post("/admin/global-pool-withdrawal-mode?enabled=" + next);
      toast.success("Global Pool payout mode: " + (next ? "AUTO — REAL BEP20" : "MANUAL"));
      reloadPayoutMode();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Unable to change payout mode");
    }
  };

  const save = async (pid) => {
    const q = parseInt(inputs[pid], 10);
    if (Number.isNaN(q) || q <= 0) { toast.error("Enter how many members to add"); return; }
    setBusy(pid);
    try {
      const r = await api.post(`/admin/global-pools/${pid}/virtual-units`, { quantity: q, reason: reasons[pid] || "" });
      toast.success(`Pool ${pid}: +${r.data.added} virtual members added · total now ${r.data.new}`);
      setInputs({ ...inputs, [pid]: "" });
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (pid) => {
    const q = parseInt(inputs[pid], 10);
    if (Number.isNaN(q) || q <= 0) { toast.error("Enter how many members to remove"); return; }
    setBusy(pid);
    try {
      const r = await api.post(`/admin/global-pools/${pid}/remove-units`, { quantity: q, reason: reasons[pid] || "undo" });
      toast.success(`Pool ${pid}: -${r.data.removed} removed · total now ${r.data.new}`);
      setInputs({ ...inputs, [pid]: "" });
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-global-pools">
      <PageTitle title="Global Pool Management" subtitle="Admin controls the 7 Global Pools. Add virtual members only; real user IDs are never Global Pool members. Every addition is time-stamped, and each user counts only virtual members added after their registration." />
      <GlassCard className="p-5" data-testid="global-pool-payout-control">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-white">Global Pool Withdrawal Control</h3>
            <p className="mt-1 text-xs text-white/50">
              {payoutMode?.auto
                ? "AUTO: Admin approval will send real USDT on BEP20 from the configured Alexa Fintech company withdrawal wallet."
                : "MANUAL: Admin makes the BEP20 transfer from the company wallet and records the real transaction hash."}
            </p>
          </div>
          <button onClick={togglePoolPayoutMode}
            className={payoutMode?.auto ? "rounded-xl btn3d-gold px-4 py-2.5 text-sm font-bold text-black" : "rounded-xl btn3d px-4 py-2.5 text-sm font-bold text-white"}
            data-testid="global-pool-payout-mode-toggle">
            {payoutMode?.auto ? <><Zap size={14} className="mr-1 inline" /> AUTO — REAL BEP20</> : <><Hand size={14} className="mr-1 inline" /> MANUAL</>}
          </button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] text-white/45">Signer</p><p className="mt-1 text-sm font-bold text-white">{payoutMode?.signer?.enabled && payoutMode?.signer?.rpc_configured && payoutMode?.signer?.key_configured ? "READY" : "NOT READY"}</p></div>
          <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] text-white/45">Company Wallet</p><p className="mt-1 truncate text-sm font-bold text-white">{payoutMode?.wallet?.address || "Not configured"}</p></div>
          <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] text-white/45">Wallet USDT / BNB</p><p className="mt-1 text-sm font-bold text-white">${payoutMode?.wallet?.usdt ?? "—"} / {payoutMode?.wallet?.bnb ?? "—"} BNB</p></div>
        </div>
      </GlassCard>
      {pendingPoolWithdrawals.length > 0 && (
        <GlassCard className="p-5" data-testid="global-pool-pending-withdrawals">
          <h3 className="mb-3 font-semibold text-white">Pending Global Pool Withdrawals</h3>
          <div className="space-y-3">
            {pendingPoolWithdrawals.map((w) => (
              <div key={w.pw_id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="grid gap-2 text-xs md:grid-cols-5 md:items-center">
                  <div><span className="text-white/40">Pool / User</span><p className="font-bold text-white">Pool {w.pool_id} · {w.member_id || "—"}</p></div>
                  <div><span className="text-white/40">Net</span><p className="font-bold gold-text">${money(w.net)} USDT</p></div>
                  <div className="md:col-span-2"><span className="text-white/40">BEP20 Address</span><p className="truncate font-mono text-white/80" title={w.wallet_address}>{w.wallet_address}</p></div>
                  <div className="flex gap-2">
                    <button onClick={() => processPoolWithdrawal(w, "paid")} disabled={payoutBusy === w.pw_id} className="rounded-lg btn3d-gold px-3 py-2 text-xs font-bold text-black disabled:opacity-50">{payoutMode?.auto ? "Pay On-Chain" : "Mark Paid"}</button>
                    <button onClick={() => processPoolWithdrawal(w, "reject")} disabled={payoutBusy === w.pw_id} className="rounded-lg btn3d px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Reject</button>
                  </div>
                </div>
                {!payoutMode?.auto && <input value={manualTx[w.pw_id] || ""} onChange={(e) => setManualTx((s) => ({ ...s, [w.pw_id]: e.target.value.trim() }))} placeholder="Real BEP20 tx hash (0x...)" className="mt-3 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs text-white outline-none" /> }
              </div>
            ))}
          </div>
        </GlassCard>
      )}
      {analytics && (
        <GlassCard className="p-5" data-testid="pool-analytics">
          <h3 className="mb-3 font-semibold text-white">Pool Analytics</h3>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[["Real IDs in Global Pool", 0], ["Virtual Members", analytics.totals.virtual_units],
              ["Total Accrued", `$${money(analytics.totals.accrued)}`], ["Pool Balances", `$${money(analytics.totals.balance)}`],
              ["Paid Net", `$${money(analytics.totals.paid)}`], ["Pending Net", `$${money(analytics.totals.pending)}`]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-white/5 p-3"><p className="text-[10px] text-white/45">{l}</p><p className="font-bold text-white">{v}</p></div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10"><th className="py-2">Pool</th><th className="py-2">Real IDs</th><th className="py-2">Virtual</th><th className="py-2">Completed</th><th className="py-2">Active ROI</th><th className="py-2">Accrued</th><th className="py-2">Balance</th><th className="py-2">Paid</th><th className="py-2">Pending</th></tr></thead>
              <tbody>
                {analytics.pools.map((p) => (
                  <tr key={p.pool_id} className="border-b border-white/5" data-testid={`analytics-row-${p.pool_id}`}>
                    <td className="py-2 text-white">Pool {p.pool_id}</td>
                    <td className="py-2">0</td>
                    <td className="py-2">{p.virtual_units}</td>
                    <td className="py-2">{p.completed_cohorts}</td>
                    <td className="py-2">{p.active_roi_cycles}</td>
                    <td className="py-2 gold-text">${money(p.total_accrued)}</td>
                    <td className="py-2">${money(p.pool_balance)}</td>
                    <td className="py-2 text-emerald-400">${money(p.paid_net)} ({p.paid_count})</td>
                    <td className="py-2 text-amber-400">${money(p.pending_net)} ({p.pending_count})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
      {loading ? <div className="skeleton h-64" /> : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {pools.map((p) => (
            <div key={p.pool_id} className="rounded-2xl p-5" style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(34,197,94,0.32)", boxShadow: "0 16px 38px -14px rgba(0,0,0,0.55)" }} data-testid={`admin-pool-${p.pool_id}`}>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: "rgba(0,0,0,0.3)" }}><Layers size={16} className="text-[var(--gold)]" /></span>
                  <div>
                    <h3 className="font-bold text-white">POOL {p.pool_id}</h3>
                    <p className="text-[11px] text-white/70">Required {p.required} · Value ${money(p.value, 0)} · ${money(p.direct_business, 0)} direct biz · {p.roi_days}d ROI · ≈${money(p.daily_roi)}/day</p>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${p.open_cohort_completed ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/60"}`}>
                  {p.open_cohort_completed ? "COHORT FILLED" : "FILLING"}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg bg-white/5 py-2"><p className="text-white/45 flex items-center justify-center gap-1"><Users size={11} />Real</p><p className="font-bold text-white" data-testid={`admin-pool-real-${p.pool_id}`}>{p.real_units}</p></div>
                <div className="rounded-lg bg-white/5 py-2"><p className="text-white/45 flex items-center justify-center gap-1"><Bot size={11} />Virtual</p><p className="font-bold text-white" data-testid={`admin-pool-virtual-${p.pool_id}`}>{p.virtual_units}</p></div>
                <div className="rounded-lg bg-white/5 py-2"><p className="text-white/45">Total</p><p className="font-bold gold-text">{p.total_units}</p></div>
                <div className="rounded-lg bg-white/5 py-2"><p className="text-white/45">Remaining</p><p className="font-bold text-white">{p.remaining}</p></div>
              </div>
              <p className="mt-2 text-[11px] text-[var(--text-dim)]">Virtual-only Global Pool · Completed: {p.open_cohort_completed ? "Yes" : "No"} · Expired direct-business windows: {p.expired_qualifications}</p>

              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="flex-1">
                  <label className="text-[11px] text-white/50">Add virtual members (real user IDs are never added to Global Pool)</label>
                  <input type="number" min="1" placeholder="e.g. 3" value={inputs[p.pool_id] ?? ""} onChange={(e) => setInputs({ ...inputs, [p.pool_id]: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[var(--gold)]/50" data-testid={`admin-pool-input-${p.pool_id}`} />
                </div>
                <input placeholder="Reason (audit)" value={reasons[p.pool_id] || ""} onChange={(e) => setReasons({ ...reasons, [p.pool_id]: e.target.value })}
                  className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[var(--gold)]/50" data-testid={`admin-pool-reason-${p.pool_id}`} />
                <button onClick={() => save(p.pool_id)} disabled={busy === p.pool_id} className="rounded-lg btn3d-gold px-4 py-2 text-sm font-bold text-black disabled:opacity-50" data-testid={`admin-pool-save-${p.pool_id}`}>Add</button>
                <button onClick={() => remove(p.pool_id)} disabled={busy === p.pool_id || p.virtual_units <= 0} title="Remove / undo units" className="grid place-items-center rounded-lg btn3d px-3 py-2 text-white disabled:opacity-40" style={{ background: "linear-gradient(180deg,#c0392b,#96271b)", border: "1px solid #7a1d13" }} data-testid={`admin-pool-remove-${p.pool_id}`}><Minus size={15} /></button>
                <button onClick={() => setAuditFor(auditFor === p.pool_id ? null : p.pool_id)} className="rounded-lg btn3d btn3d-dark px-3 py-2 text-sm" data-testid={`admin-pool-audit-${p.pool_id}`}><History size={15} /></button>
              </div>

              {auditFor === p.pool_id && (
                <div className="mt-3 max-h-40 space-y-1 overflow-y-auto rounded-lg bg-black/20 p-3 text-[11px]">
                  {(audit?.virtual_history || []).length ? audit.virtual_history.map((h, i) => (
                    <p key={i} className="text-white/70">{(h.at || "").slice(0, 16).replace("T", " ")} · {h.old}→{h.new} ({h.diff >= 0 ? "+" : ""}{h.diff}) by {h.by} {h.reason ? `· ${h.reason}` : ""}</p>
                  )) : <p className="text-white/40">No virtual-unit history yet.</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
